import { useEffect, useState } from 'react';
import classNames from 'classnames';

import { Classes } from 'types/Classes';
import { useDebounce } from 'hooks/useDebounce';
import InputNumber from '@templates/InputNumber';

import classes from './InputRange.module.scss';

const formInputDebounce = 1000;

type Props = {
  name: string;
  suffix: string;
  firstValue: string;
  secondValue: string;
  firstPlaceholder: string;
  secondPlaceholder: string;
  isFirstWrong?: boolean;
  isSecondWrong?: boolean;
  withoutMargin?: boolean;
  classes?: Classes<'root' | 'inputRoot' | 'inputSuffix'>;
  onChange: (values: { minValue: string | number; maxValue: string | number }) => void;
};

const InputRange = ({
  name,
  suffix,
  firstValue,
  secondValue,
  firstPlaceholder,
  secondPlaceholder,
  isFirstWrong = false,
  isSecondWrong = false,
  withoutMargin = false,
  classes: customClasses,
  onChange,
}: Props): JSX.Element => {
  const [minValue, setMinValue] = useState(firstValue ? Number(firstValue) : '');
  const [maxValue, setMaxValue] = useState(secondValue ? Number(secondValue) : '');

  useDebounce(
    () => {
      if (firstValue !== minValue || secondValue !== maxValue) {
        onChange({ minValue, maxValue });
      }
    },
    [minValue, maxValue],
    formInputDebounce
  );

  useEffect(() => {
    setMinValue(firstValue);
  }, [firstValue]);

  useEffect(() => {
    setMaxValue(secondValue);
  }, [secondValue]);

  return (
    <div className={classNames(classes.wrapper, customClasses?.root, { [classes.withoutMargin]: withoutMargin })}>
      <InputNumber
        name={`${name}_from`}
        classes={{ root: customClasses?.inputRoot, suffix: customClasses?.inputSuffix }}
        suffix={suffix}
        placeholder={firstPlaceholder}
        isWrong={isFirstWrong}
        value={minValue}
        onChange={setMinValue}
      />

      <div className={classes.separator}>—</div>

      <InputNumber
        name={`${name}_to`}
        classes={{ root: customClasses?.inputRoot, suffix: customClasses?.inputSuffix }}
        suffix={suffix}
        placeholder={secondPlaceholder}
        isWrong={isSecondWrong}
        value={maxValue}
        onChange={setMaxValue}
      />
    </div>
  );
};

export default InputRange;
